import { useMemo, useState, type CSSProperties } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { zeitInZone } from '@famora/core/recurrence/zeit'
import { ApiFehler, hole, schicke } from '../lib/api'
import {
  datumHeute,
  datumKurz,
  datumLesen,
  datumVon,
  monatsFenster,
  tageAddieren,
  zeitKurz,
} from '../lib/datum'
import type { KalenderInstanz, MitgliedKurz } from '../lib/modelle'
import { mitgliedsFarbe } from '../session/logic'
import { useDarf, useSitzung } from '../session/SessionProvider'
import { useFab } from '../shell/FabSlot'
import { Button, Card, IconButton, ListItem, Select, Sheet, Switch, TextField } from '../ui'
import { EintragAktionen } from './EintragAktionen'
import { PersonenAuswahl } from './PersonenAuswahl'

interface KalenderAntwort {
  instanzen: KalenderInstanz[]
}

const MONATSNAMEN = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
]
const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

/** Der Montag der Woche, in der dieser Tag liegt - das Raster beginnt montags. */
function rasterStart(erster: string): string {
  const d = datumLesen(erster)
  // getDay(): 0 = Sonntag. Auf ISO umrechnen, sonst beginnt die Woche falsch.
  const iso = d.getDay() === 0 ? 7 : d.getDay()
  return tageAddieren(erster, -(iso - 1))
}

export function Kalender() {
  const { abfrage } = useSitzung()
  const preview = abfrage.data && !abfrage.data.setupRequired && abfrage.data.preview
  const zeitzone =
    abfrage.data && !abfrage.data.setupRequired ? abfrage.data.household.timezone : 'Europe/Berlin'
  const qc = useQueryClient()
  const darfAnlegen = useDarf('calendar.create')
  const darfAendern = useDarf('calendar.edit')

  const [anker, setAnker] = useState(datumHeute())
  const [gewaehlt, setGewaehlt] = useState(datumHeute())
  const [offen, setOffen] = useState(false)
  const [titel, setTitel] = useState('')
  const [personenIds, setPersonenIds] = useState<string[]>([])
  const [freq, setFreq] = useState('')
  const [interval, setInterval] = useState(1)
  const [ganztaegig, setGanztaegig] = useState(false)
  const [beginn, setBeginn] = useState('09:00')
  const [dauer, setDauer] = useState(60)
  const [fehler, setFehler] = useState<string | null>(null)

  useFab(
    darfAnlegen && !preview
      ? {
          label: 'Termin anlegen',
          icon: 'plus',
          onClick: () => {
            setOffen(true)
          },
        }
      : null,
  )

  const start = rasterStart(monatsFenster(anker).von)
  const fenster = { von: start, bis: tageAddieren(start, 41) }
  const schluessel = ['calendar', fenster.von, fenster.bis] as const

  const liste = useQuery({
    queryKey: schluessel,
    queryFn: () => hole<KalenderAntwort>(`/api/calendar?von=${fenster.von}&bis=${fenster.bis}`),
  })
  const mitglieder = useQuery({
    queryKey: ['members'],
    queryFn: () => hole<MitgliedKurz[]>('/api/members'),
  })

  const anlegen = useMutation({
    mutationFn: () => {
      if (ganztaegig) {
        return schicke('POST', '/api/calendar', {
          title: titel.trim(),
          participants: personenIds,
          ...(freq ? { recur: { freq, interval } } : {}),
          allDayOn: gewaehlt,
        })
      }
      // Die Eingabe meint die Ortszeit des Haushalts. Die kann von der
      // Browser-Zeitzone abweichen, etwa unterwegs oder nach einem Umzug.
      const start = zeitInZone(gewaehlt, `${beginn}:00`, zeitzone)
      const ende = new Date(start.getTime() + dauer * 60_000)
      return schicke('POST', '/api/calendar', {
        title: titel.trim(),
        participants: personenIds,
        ...(freq ? { recur: { freq, interval } } : {}),
        startsAt: start.toISOString(),
        endsAt: ende.toISOString(),
      })
    },
    onSuccess: async () => {
      setTitel('')
      setOffen(false)
      setFehler(null)
      await qc.invalidateQueries({ queryKey: ['calendar'] })
      await qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
    onError: (e: unknown) =>
      setFehler(e instanceof ApiFehler ? e.message : 'Der Termin konnte nicht angelegt werden.'),
  })

  const ausfallen = useMutation({
    mutationFn: (i: KalenderInstanz) =>
      schicke(
        'PATCH',
        `/api/calendar/${i.eventId}/occurrence/${i.seriesOccurrenceDate ?? i.occurrenceDate}`,
        {
          status: 'skipped',
        },
      ),
    onSuccess: async () => {
      setFehler(null)
      await qc.invalidateQueries({ queryKey: ['calendar'] })
    },
    onError: (e: unknown) =>
      setFehler(e instanceof ApiFehler ? e.message : 'Der Termin konnte nicht abgesagt werden.'),
  })

  const personen = new Map((mitglieder.data ?? []).map((m) => [m.id, m]))
  const instanzen = liste.data?.instanzen ?? []

  const proTag = useMemo(() => {
    const karte = new Map<string, KalenderInstanz[]>()
    for (const i of instanzen) {
      const liste = karte.get(i.occurrenceDate) ?? []
      liste.push(i)
      karte.set(i.occurrenceDate, liste)
    }
    return karte
  }, [instanzen])

  // 6 Wochen decken jeden Monat ab, auch einen, der sonntags endet.
  const tage = Array.from({ length: 42 }, (_, n) => tageAddieren(start, n))
  const monatDesAnkers = datumLesen(anker).getMonth()
  const desTages = proTag.get(gewaehlt) ?? []

  const verschiebe = (monate: number) => {
    const d = datumLesen(anker)
    const neu = new Date(d.getFullYear(), d.getMonth() + monate, 1)
    setAnker(datumVon(neu))
  }

  if (liste.isError)
    return (
      <p className="fehler" role="alert">
        Die Daten konnten nicht geladen werden.{' '}
        <Button
          variant="text"
          onClick={() => {
            void liste.refetch()
          }}
        >
          Erneut versuchen
        </Button>
      </p>
    )

  return (
    <>
      <div className="seitenkopf">
        <div>
          <h1>Kalender</h1>
          <p>
            {MONATSNAMEN[monatDesAnkers]} {datumLesen(anker).getFullYear()}
          </p>
        </div>
        <div className="reihe reihe--eng">
          <IconButton
            name="chevron-left"
            label="Voriger Monat"
            onClick={() => {
              verschiebe(-1)
            }}
          />
          <Button
            variant="text"
            onClick={() => {
              setAnker(datumHeute())
              setGewaehlt(datumHeute())
            }}
          >
            Heute
          </Button>
          <IconButton
            name="chevron-right"
            label="Nächster Monat"
            onClick={() => {
              verschiebe(1)
            }}
          />
        </div>
      </div>

      <Sheet
        offen={offen}
        titel={`Termin am ${datumKurz(gewaehlt)}`}
        onSchliessen={() => {
          setOffen(false)
        }}
      >
        <form
          className="stack-4"
          onSubmit={(e) => {
            e.preventDefault()
            anlegen.mutate()
          }}
        >
          <TextField
            id="termin-titel"
            label="Worum geht es?"
            wert={titel}
            onWert={setTitel}
            required
            autoFocus
          />
          <Switch checked={ganztaegig} onChange={setGanztaegig} label="Ganztägig" />
          {!ganztaegig ? (
            <div className="reihe reihe--felder">
              <TextField
                id="termin-beginn"
                label="Beginn"
                type="time"
                wert={beginn}
                onWert={setBeginn}
                required
              />
              <TextField
                id="termin-dauer"
                label="Dauer in Minuten"
                type="number"
                min={5}
                step={5}
                wert={String(dauer)}
                onWert={(w) => {
                  setDauer(Math.max(5, Number(w)))
                }}
              />
            </div>
          ) : null}
          <div className="reihe reihe--felder">
            <Select id="termin-wiederholung" label="Wiederholung" wert={freq} onWert={setFreq}>
              <option value="">Keine</option>
              <option value="daily">Täglich</option>
              <option value="weekly">Wöchentlich</option>
              <option value="monthly">Monatlich</option>
            </Select>
            {freq ? (
              <TextField
                id="termin-intervall"
                label="Intervall"
                type="number"
                min={1}
                step={1}
                wert={String(interval)}
                onWert={(w) => {
                  setInterval(Number(w))
                }}
                required
              />
            ) : null}
          </div>
          <PersonenAuswahl wert={personenIds} setzen={setPersonenIds} />
          {fehler ? (
            <p className="fehler" role="alert">
              {fehler}
            </p>
          ) : null}
          <div className="blatt__fuss-inline">
            <span />
            <Button type="submit" laeuft={anlegen.isPending} disabled={!titel.trim()}>
              {anlegen.isPending ? 'Wird angelegt …' : 'Speichern'}
            </Button>
          </div>
        </form>
      </Sheet>

      {fehler && !offen ? (
        <p className="fehler" role="alert">
          {fehler}
        </p>
      ) : null}

      <div className="monat" role="grid" aria-label="Monatsübersicht">
        {WOCHENTAGE.map((t) => (
          <div key={t} className="monat__wochentag" role="columnheader">
            {t}
          </div>
        ))}
        {tage.map((tag) => {
          const eintraege = proTag.get(tag) ?? []
          const fremd = datumLesen(tag).getMonth() !== monatDesAnkers
          const istHeute = tag === datumHeute()
          return (
            <button
              key={tag}
              type="button"
              role="gridcell"
              className={`monat__tag${fremd ? ' monat__tag--fremd' : ''}${istHeute ? ' monat__tag--heute' : ''}`}
              aria-pressed={tag === gewaehlt}
              aria-label={`${datumKurz(tag)}, ${eintraege.length} Termine`}
              onClick={() => {
                setGewaehlt(tag)
              }}
            >
              <span className="monat__kreis">{datumLesen(tag).getDate()}</span>
              <span className="monat__punkte" aria-hidden="true">
                {eintraege.slice(0, 3).map((i) => {
                  const erster = i.teilnehmer[0]
                  const m = erster ? personen.get(erster) : undefined
                  const farbe = m ? mitgliedsFarbe(m.color, m.id) : 'var(--c-outline)'
                  return (
                    <span
                      key={`${i.eventId}-${i.seriesOccurrenceDate ?? i.occurrenceDate}-${i.startsAt ?? 'ganztag'}`}
                      className="monat__punkt"
                      style={{ '--stripe': farbe } as CSSProperties}
                    />
                  )
                })}
              </span>
            </button>
          )
        })}
      </div>

      <Card titel={datumKurz(gewaehlt)}>
        {desTages.length === 0 ? (
          <p className="unterzeile">Nichts eingetragen.</p>
        ) : (
          <ul className="liste liste--gruppe">
            {desTages.map((i) => {
              const erster = i.teilnehmer[0]
              const m = erster ? personen.get(erster) : undefined
              const farbe = m ? mitgliedsFarbe(m.color, m.id) : undefined
              const von = zeitKurz(i.startsAt, zeitzone)
              const bis = zeitKurz(i.endsAt, zeitzone)
              const namen = i.teilnehmer
                .map((id) => personen.get(id)?.displayName)
                .filter(Boolean)
                .join(', ')
              const neben = [
                i.allDayOn ? 'Ganztägig' : von && bis ? `${von} – ${bis}` : von,
                i.location,
                i.wiederkehrend ? 'wiederholt sich' : null,
                i.geaendert ? 'geändert' : null,
                namen || null,
              ]
                .filter(Boolean)
                .join(' · ')

              return (
                <li
                  key={`${i.eventId}-${i.seriesOccurrenceDate ?? i.occurrenceDate}-${i.occurrenceDate}-${i.startsAt ?? 'ganztag'}`}
                >
                  <ListItem
                    as="div"
                    titel={i.title}
                    unterzeile={neben}
                    farbe={farbe}
                    folgend={
                      darfAendern && !preview && i.wiederkehrend ? (
                        <Button
                          variant="text"
                          disabled={ausfallen.isPending}
                          onClick={() => {
                            ausfallen.mutate(i)
                          }}
                        >
                          Fällt aus
                        </Button>
                      ) : undefined
                    }
                  />
                  <EintragAktionen
                    modul="calendar"
                    id={i.eventId}
                    titel={i.title}
                    notizen={i.notes}
                    serie={i.wiederkehrend}
                    personen={i.teilnehmer}
                    kalender={{
                      occurrenceDate: i.seriesOccurrenceDate ?? i.occurrenceDate,
                      displayDate: i.occurrenceDate,
                      startsAt: i.startsAt,
                      endsAt: i.endsAt,
                      allDayOn: i.allDayOn,
                      allDayBis: i.allDayBis,
                      location: i.location,
                      visibility: i.visibility,
                      wiederholung: i.wiederholung ?? null,
                      timezone: zeitzone,
                    }}
                  />
                </li>
              )
            })}
          </ul>
        )}
      </Card>
    </>
  )
}
